import type {
  AssignmentDetailApiResponse,
  AssignmentListApiResponse
} from "@ts/interfaces/assignments/api/assignmentsApiInterface";
import { del, get, post } from "./httpClient";

export const getAssignmentsList = async (
  gradeCode: number,
  term?: number
): Promise<AssignmentListApiResponse> => {
  const url = term
    ? `/assignments/grade/${gradeCode}?term=${term}`
    : `/assignments/grade/${gradeCode}`;
  return await get<AssignmentListApiResponse>(url, { suppressGlobalErrors: true });
};

const isEmptyObject = (v: unknown): boolean =>
  v != null && typeof v === "object" && Object.keys(v).length === 0;

export const getAssignmentDetail = async (
  assignmentId: string,
  gradeCode: number,
  term: number
): Promise<AssignmentDetailApiResponse> => {
  const res = await get<AssignmentDetailApiResponse>(
    `/assignments/${assignmentId}?grade_code=${gradeCode}&term=${term}`
  );
  const a = res.assignment;
  if (isEmptyObject(a.assignment_blocks)) a.assignment_blocks = undefined;
  if (isEmptyObject(a.rubric_blocks)) a.rubric_blocks = undefined;
  if (isEmptyObject(a.support_guide_blocks)) a.support_guide_blocks = undefined;
  return res;
};

export const createSubmission = async (
  assignmentId: string
): Promise<{ data: { submission_uuid: string } }> => {
  return await post<{ data: { submission_uuid: string } }>(
    `/assignments/${assignmentId}/submissions`
  );
};

export const createExemption = async (
  assignmentId: string,
  reason: string
): Promise<{ message: string }> => {
  return await post<{ message: string }>(
    `/assignments/${assignmentId}/exemption`,
    { reason }
  );
};

export const deleteExemption = async (
  exemptionId: string
): Promise<{ message: string }> => {
  return await del<{ message: string }>(
    `/assignments/exemptions/${exemptionId}`
  );
};

/**
 * Request a pre-signed S3 upload URL for a submission asset.
 * The MFE then PUTs the file directly to S3 using the returned URL.
 */
export const createSubmissionAsset = async (
  submissionUuid: string,
  deliverableUuid: string,
  fileName: string,
  fileSize: number,
  mimeType: string
): Promise<{
  data: {
    file_upload_url: string;
    file_upload_uuid: string;
    file_name: string;
    file_asset_id: number;
  };
}> => {
  return await post(
    `/assignments/submissions/${submissionUuid}/asset`,
    {
      deliverable_uuid: deliverableUuid,
      file_name: fileName,
      file_size: fileSize,
      mime_type: mimeType
    }
  );
};

/**
 * Upload a file directly to S3 using a pre-signed PUT URL.
 * This is called after createSubmissionAsset returns the signed URL.
 */
export const uploadFileToSignedUrl = async (
  signedUrl: string,
  file: File
): Promise<void> => {
  const response = await fetch(signedUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type || "application/octet-stream"
    }
  });
  if (!response.ok) {
    throw new Error(
      `Upload failed: ${response.status} ${response.statusText}`
    );
  }
};

export const assignmentsApi = {
  getAssignmentsList,
  getAssignmentDetail,
  createSubmission,
  createExemption,
  deleteExemption,
  createSubmissionAsset,
  uploadFileToSignedUrl
};
